import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Spinner, Surface, PageHeader } from "@/components/common";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SlidersHorizontal, Loader2, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, PLAN_LABELS } from "./parts";

const PLAN_OPTIONS = ["gratis", "start", "pro", "business"];
const PAGE = 15;

export default function AdminUsers() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [planF, setPlanF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [showTest, setShowTest] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [plan, setPlan] = useState("gratis");
  const [creditMode, setCreditMode] = useState("plan");
  const [credits, setCredits] = useState("");
  const [isTest, setIsTest] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/admin/stats").then(({ data }) => setData(data)).catch((e) => setErr(apiError(e)));
  useEffect(() => { load(); }, []);

  const isTestUser = (u) => u.role === "admin" || u.is_test === true || /@pointtaken\.ai$/i.test(u.email || "");

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.users;
    if (!showTest) list = list.filter((u) => !isTestUser(u));
    if (planF !== "all") list = list.filter((u) => u.plan === planF);
    if (statusF !== "all") list = list.filter((u) => (u.subscription_status || "—") === statusF);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((u) => (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s) || (u.company_name || "").toLowerCase().includes(s));
    }
    return list;
  }, [data, q, planF, statusF, showTest]);

  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const view = rows.slice((page - 1) * PAGE, page * PAGE);
  useEffect(() => { setPage(1); }, [q, planF, statusF, showTest]);

  const openEdit = (u) => {
    setEditing(u); setConfirm(null);
    setPlan(u.plan || "gratis");
    setIsTest(!!u.is_test);
    if (u.credit_override != null) { setCreditMode("custom"); setCredits(String(u.credit_override)); }
    else { setCreditMode("plan"); setCredits(""); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { plan, is_test: isTest };
      if (creditMode === "plan") payload.clear_override = true;
      else payload.credit_override = Math.max(0, parseInt(credits || "0", 10));
      const { data: fresh } = await api.patch(`/admin/users/${editing.id}`, payload);
      setData((d) => ({ ...d, users: d.users.map((x) => (x.id === fresh.id ? { ...x, ...fresh } : x)) }));
      toast.success(`${fresh.name || fresh.email} opdateret`);
      setEditing(null);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      if (confirm === "company") { await api.delete(`/admin/companies/${editing.company_id}`); toast.success("Virksomhed slettet"); }
      else { await api.delete(`/admin/users/${editing.id}`); toast.success("Bruger slettet"); }
      setEditing(null); setConfirm(null); await load();
    } catch (e) { toast.error(apiError(e)); } finally { setDeleting(false); }
  };

  const exportCsv = () => {
    const head = ["Navn", "Email", "Virksomhed", "Kilde", "Plan", "Status", "Kreditter", "Oprettet", "Seneste aktivitet"];
    const lines = rows.map((u) => [u.name, u.email, u.company_name || "", u.source || "Ukendt", u.plan, u.subscription_status || "", `${u.credits_used}/${u.credits_unlimited ? "unlimited" : u.credits_limit}`, u.created_at || "", [u.last_active_at, u.last_login].filter(Boolean).sort().pop() || ""].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "aicom-brugere.csv"; a.click();
  };

  if (err) return <div className="py-16 text-center text-muted-foreground">{err}</div>;
  if (!data) return <Spinner label="Henter brugere..." />;

  return (
    <div>
      <PageHeader overline="Administrator" title="Brugere" subtitle="Administrér brugere, virksomheder, planer og credits."
        action={<Button variant="outline" size="sm" onClick={exportCsv} data-testid="users-export">Eksportér CSV</Button>} />

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søg navn, e-mail, virksomhed" className="pl-9 h-9" data-testid="users-search" />
        </div>
        <Select value={planF} onValueChange={setPlanF}><SelectTrigger className="w-[130px] h-9" data-testid="users-plan-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle planer</SelectItem>{PLAN_OPTIONS.map((p) => <SelectItem key={p} value={p}>{PLAN_LABELS[p]}</SelectItem>)}</SelectContent></Select>
        <Select value={statusF} onValueChange={setStatusF}><SelectTrigger className="w-[140px] h-9" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle statusser</SelectItem><SelectItem value="trialing">Prøveperiode</SelectItem><SelectItem value="active">Aktiv</SelectItem><SelectItem value="past_due">Forfalden</SelectItem><SelectItem value="canceled">Opsagt</SelectItem><SelectItem value="—">Ingen</SelectItem></SelectContent></Select>
        <div className="flex items-center gap-2 px-3 h-9 rounded-md border border-border"><span className="text-xs text-muted-foreground">Vis test/admin</span>
          <Switch checked={showTest} onCheckedChange={setShowTest} data-testid="users-show-test" /></div>
      </div>

      <Surface testid="admin-users">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Bruger</TableHead><TableHead>Virksomhed</TableHead><TableHead>Kilde</TableHead><TableHead>Plan</TableHead>
              <TableHead>Status</TableHead><TableHead>Kreditter</TableHead><TableHead>Oprettet</TableHead>
              <TableHead>Seneste aktivitet</TableHead><TableHead>Trial udløber</TableHead><TableHead className="text-right">Handling</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {view.length === 0 ? <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">Ingen brugere matcher</TableCell></TableRow> :
              view.map((u) => (
                <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                  <TableCell><div className="font-medium flex items-center gap-2">{u.name}{isTestUser(u) && <Badge variant="outline" className="text-[9px]">test</Badge>}</div><div className="text-xs text-muted-foreground">{u.email}</div></TableCell>
                  <TableCell className="text-muted-foreground">{u.company_name ? (<span className="inline-flex items-center gap-1.5">{u.company_name}{u.company_pending && <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-500" data-testid={`company-pending-${u.id}`}>afventer</Badge>}</span>) : "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] whitespace-nowrap" data-testid={`user-source-${u.id}`}>{u.source || "Ukendt"}</Badge></TableCell>
                  <TableCell>{u.email_verified === false ? <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-500" data-testid={`user-plan-pending-${u.id}`}>Afventer</Badge> : <Badge variant="secondary">{PLAN_LABELS[u.plan] || u.plan}</Badge>}</TableCell>
                  <TableCell className="text-xs">{u.subscription_status || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{u.credits_unlimited ? `${u.credits_used} / ∞` : `${u.credits_used} / ${u.credits_limit}`}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{fmtDate(u.created_at)}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{fmtDate([u.last_active_at, u.last_login].filter(Boolean).sort().pop(), true)}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{u.trial_ends_at ? fmtDate(u.trial_ends_at) : "—"}</TableCell>
                  <TableCell className="text-right">
                    {u.role === "admin" ? <span className="text-xs text-muted-foreground">Admin</span> :
                      <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid={`admin-edit-${u.id}`} className="gap-1.5"><SlidersHorizontal className="h-3.5 w-3.5" /> Rediger</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-muted-foreground">{rows.length} brugere</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Forrige</Button>
              <span className="text-muted-foreground">{page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Næste</Button>
            </div>
          </div>
        )}
      </Surface>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="admin-edit-dialog">
          <DialogHeader><DialogTitle>Rediger {editing?.name || editing?.email}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            <div><Label className="mb-2 block">Pakke</Label>
              <Select value={plan} onValueChange={setPlan}><SelectTrigger data-testid="admin-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PLAN_OPTIONS.map((p) => <SelectItem key={p} value={p}>{PLAN_LABELS[p]}</SelectItem>)}</SelectContent></Select>
              <p className="text-xs text-muted-foreground mt-1.5">Lokal planændring påvirker ikke Stripe-abonnementet.</p></div>
            <div><Label className="mb-2 block">Kreditter pr. måned</Label>
              <Select value={creditMode} onValueChange={setCreditMode}><SelectTrigger data-testid="admin-credit-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="plan">Følg pakkens standard</SelectItem><SelectItem value="custom">Tilpasset antal</SelectItem></SelectContent></Select>
              {creditMode === "custom" && <Input type="number" min="0" value={credits} onChange={(e) => setCredits(e.target.value)} placeholder="fx 250" className="mt-3" data-testid="admin-credit-input" />}</div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <div><Label>Testbruger</Label><p className="text-xs text-muted-foreground">Skjules fra kommercielle KPI'er</p></div>
              <Switch checked={isTest} onCheckedChange={setIsTest} data-testid="admin-is-test" /></div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-3 sm:justify-between">
            <div>{confirm ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="admin-delete-confirm">
                <span className="text-sm text-muted-foreground">Sikker?</span>
                <Button variant="destructive" size="sm" onClick={doDelete} disabled={deleting} data-testid="admin-delete-confirm-btn" className="gap-1.5">{deleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Ja, slet {confirm === "company" ? "virksomhed" : "bruger"}</Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirm(null)}>Fortryd</Button></div>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setConfirm("user")} data-testid="admin-delete-user-btn" className="gap-1.5 text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /> Slet bruger</Button>
                {editing?.company_name && <Button variant="outline" size="sm" onClick={() => setConfirm("company")} data-testid="admin-delete-company-btn" className="gap-1.5 text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /> Slet virksomhed</Button>}</div>
            )}</div>
            <div className="flex items-center gap-2 justify-end">
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Annullér</Button>
              <Button onClick={save} disabled={saving} data-testid="admin-save-btn" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Gem</Button></div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
